import { FunctionComponent, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as graphql from 'generated/graphql';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

import useSupportedLanguageIDs from './useSupportedLanguageIDs';

function useAllSupportedLanguages(): { languageIDs?: string[]; loading: boolean } {
	const { i18n } = useTranslation();
	const languageIDs = useSupportedLanguageIDs();
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		(async () => {
			if (!languageIDs) {
				return;
			}

			await i18n.loadLanguages(languageIDs.map((id) => id.split('_')[0]));

			setLoading(false);
		})();
	}, [languageIDs?.join(',')]);

	return { languageIDs: languageIDs, loading };
}

export function withLocalizedTranslation<T extends FunctionComponent<P>, P>(target: T) {
	return (props: PropsWithChildren<P>) => {
		const { localizedT, loading } = useLocalizedTranslation();

		const Target = target as any;

		return loading ? (
			<Spinner isLoading size="18px" />
		) : (
			<Target {...props} localizedT={localizedT} />
		);
	};
}

export default function useLocalizedTranslation(): {
	localizedT: (key: string, options?: Record<string, any>) => graphql.LocalizedString;
	loading: boolean;
} {
	const { t } = useTranslation();
	const { languageIDs, loading } = useAllSupportedLanguages();

	return {
		localizedT: (key, options) => {
			if (loading || !languageIDs) {
				throw new Error("Can't localize while translations are loading.");
			}

			return languageIDs.map((languageID) => ({
				languageID,
				text: t(key, {
					...options,
					lng: languageID.split('_')[0],
				}),
			}));
		},
		loading,
	};
}
